import React, { useMemo } from 'react';
import classNames from 'classnames';
import { isMobile } from 'react-device-detect';
import moment from 'moment';
import './hosDaily.scss';
import { HosDailyInfo } from 'src/features/hosLogs/hosLog/types';

import { HosDailyBox } from 'src/components/HosDaily/HosDailyBox/HosDailyBox';
import { checkDataValue } from 'src/utils/checkVariableValue';

interface Props {
  hosDaily: HosDailyInfo;
  recordDate: string;
  timeZone: string;
}

export const HosDaily: React.FC<Props> = ({ hosDaily, recordDate, timeZone }) => {
  const { exemptStatus = '', exemptReason = '' } = hosDaily;
  const dailyHeaderClassNames = classNames('daily-header', { 'is-desktop': !isMobile });
  const timeZoneAbbr = useMemo(
    () =>
      moment()
        .tz(timeZone)
        .format('z'),
    [timeZone],
  );

  // Helper Function
  const replaceWithAsterisk = (str: string) => str.replace(/.(?=.{4,}$)/g, '*');
  const renderTractor = (keys: string[]) =>
    keys.length ? keys.map((key, idx) => <div key={idx}>{key}</div>) : '--';
  return (
    <div className={dailyHeaderClassNames}>
      <div className="daily-header-item">
        <div className="daily-header-item__title">Date</div>
        <div className="daily-header-boxes">
          <HosDailyBox subTitle="Record date">{recordDate}</HosDailyBox>
          <HosDailyBox subTitle="Log Day Start Time">
            {moment.tz(hosDaily.periodStartTime, hosDaily.timezone).format('HH:mm')}
          </HosDailyBox>
          <HosDailyBox subTitle="Timezone" addClassName="short-cell">
            {hosDaily.timezone || timeZoneAbbr}
          </HosDailyBox>
          <HosDailyBox subTitle="ELD Exempt">
            {exemptStatus === 'Exempt' ? `Exempt, "${exemptReason}"` : 'Not Exempt'}
          </HosDailyBox>
        </div>
      </div>
      <div className="daily-header-item">
        <div className="daily-header-item__title">Driver</div>
        <div className="daily-header-boxes">
          <HosDailyBox subTitle="Driver Name">{checkDataValue(hosDaily.driver.name)}</HosDailyBox>
          <HosDailyBox subTitle="Driver ID">{checkDataValue(hosDaily.driver.driverId)}</HosDailyBox>
          <HosDailyBox subTitle="Driver License Number">
            {hosDaily.driver.licenseNumber
              ? replaceWithAsterisk(hosDaily.driver.licenseNumber)
              : '--'}
          </HosDailyBox>
          <HosDailyBox subTitle="Driver License State">
            {checkDataValue(hosDaily.driver.licenseState)}
          </HosDailyBox>
          <HosDailyBox subTitle="Co-Driver Name">
            {checkDataValue(hosDaily.driver.coDriver)}
          </HosDailyBox>
        </div>
      </div>
      <div className="daily-header-item">
        <div className="daily-header-item__title">Tractor</div>
        <div className="daily-header-boxes">
          <HosDailyBox subTitle="Truck Tractor ID">
            {renderTractor(hosDaily?.tractor?.map(t => t.id) ?? [])}
          </HosDailyBox>
          <HosDailyBox subTitle="Truck Tractor VIN">
            {renderTractor(hosDaily?.tractor?.map(t => t.vin) ?? [])}
          </HosDailyBox>
        </div>
      </div>
      <div className="daily-header-item">
        <div className="daily-header-item__title">Carrier</div>
        <div className="daily-header-boxes">
          <HosDailyBox subTitle="Carrier">{checkDataValue(hosDaily.carrier.carrier)}</HosDailyBox>
          <HosDailyBox subTitle="USDOT #">{checkDataValue(hosDaily.carrier.udot)}</HosDailyBox>
        </div>
      </div>
      <div className="daily-header-item">
        <div className="daily-header-item__title">ELD</div>
        <div className="daily-header-boxes">
          <HosDailyBox subTitle="Provider">{checkDataValue(hosDaily.eld.provider)}</HosDailyBox>
          <HosDailyBox subTitle="Registration ID">
            {checkDataValue(hosDaily.eld.registrationId)}
          </HosDailyBox>
          <HosDailyBox subTitle="Identifier">{checkDataValue(hosDaily.eld.identifier)}</HosDailyBox>
        </div>
      </div>
    </div>
  );
};
